import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserContext } from '../contexts/UserContext';
import { authManager } from '../utils/auth';
import { getSetupToken, clearSetupToken } from '../utils/setupTokenStore';


const Base_Url = process.env.REACT_APP_API_BASE_URL;

const Authentication = () => {
    const [qrCode, setQrCode] = useState('');
    const [qrError, setQrError] = useState(false);
    const [mfaToken, setMfaToken] = useState('');
    const navigate = useNavigate();
    const { setUser } = useContext(UserContext);

    // This screen only runs right after Password.js completes registration.
    // completeRegistration returns a short-lived setup token in the response
    // body (no cookie is set yet, since MFA isn't verified) - Password.js
    // hands it here via an in-memory module variable (setupTokenStore.js),
    // so it never touches localStorage/sessionStorage or window.history.state.
    const setupToken = getSetupToken();

    useEffect(() => {
        const fetchMfaQRCode = async () => {
            if (!setupToken) return;

            // Clear any stale accessToken/refreshToken cookie from a previous
            // session in this browser first: verifyToken reads the cookie before
            // falling back to this Authorization header, so a lingering cookie
            // would silently make the backend act on the wrong (old) identity
            // instead of the just-registered user's setup token. Best-effort -
            // don't let a network hiccup on this call block the QR code fetch.
            await fetch(`${Base_Url}/users/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});

            try {
                // No cookie involved for this call - it neither reads nor sets one -
                // so credentials are intentionally left out.
                const response = await fetch(`${Base_Url}/users/generate-mfa`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${setupToken}` },
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();
                setQrCode(data.result.qrCodeDataURL);
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.error('Error generating MFA QR code:', error);
                }
                setQrError(true);
                toast.error('Could not load the MFA QR code. Please try again or contact support.');
            }
        };

        fetchMfaQRCode();
    }, [setupToken]);

    const handleMfaSubmit = async (e) => {
        e.preventDefault();

        const userId = localStorage.getItem('userId');

        if (!setupToken || !userId) {
            toast.error("Missing user session. Please log in again.");
            return;
        }

        if (!mfaToken.trim()) {
            toast.warning("Please enter the MFA code.");
            return;
        }

        try {
            const response = await fetch(`${Base_Url}/users/verify-mfa`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${setupToken}`,
                },
                body: JSON.stringify({
                    mfaToken,
                    userId
                })
            });

            const data = await response.json();

            if (process.env.NODE_ENV === 'development') {
                console.log('MFA Response:', { 
                    status: response.status, 
                    statusCode: data.status_code,
                    hasResult: !!data.result,
                    hasToken: !!(data.result?.token),
                    hasRefreshToken: !!(data.result?.refreshToken)
                });
            }

            if (data.status_code === 200) {
                const result = data.result;

                if (process.env.NODE_ENV === 'development') {
                    console.log('MFA Success - Token received:', !!result.token);
                    console.log('MFA Success - User data:', { id: result.user.id, name: result.user.name });
                }

                // Store only display-safe data in localStorage
                // Standardize field access - handle both response shapes
                const userId = result.userId || result.user?.id;
                const userName = result.name || result.user?.name;
                const userEmail = result.email || result.user?.email;
                const userRole = result.role || result.user?.role;

                localStorage.setItem('userId', userId);
                localStorage.setItem('name', userName);
                localStorage.setItem('userEmail', userEmail);
                localStorage.setItem('role', userRole);

                // verify-mfa set the httpOnly auth cookies on this response -
                // just flip the local "logged in" flag and start the session.
                authManager.markLoggedIn();
                authManager.init();

                // The setup token has done its job (generate-mfa/verify-mfa) -
                // drop it from memory now rather than leaving it around.
                clearSetupToken();

                setUser({ 
                    name: userName, 
                    userId: userId,
                    email: userEmail,
                    role: userRole
                });

                toast.success(data.message || "MFA verified successfully!");

                // Small delay to ensure token is properly set before navigation
                setTimeout(() => {
                    navigate('/dashboard');
                }, 100);
            } else {
                toast.error(data.message || "MFA verification failed.");
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('MFA Submit Error:', error);
            }
            const errorMsg = error.response?.data?.message || 
                           error.response?.data?.error?.message || 
                           "MFA verification failed.";
            toast.error(errorMsg);
        }
    };

    return (
        <div className='hold-transition login-page accent-custom'>
            <ToastContainer autoClose={3000} />
            <div className="login-box login_div">
                <div className="card card-outline card-primary">
                    <div className="card-header text-center">
                        <Link to="/" className="login_logo">
                            <img src="assets/img/logo.png" alt='logo.png' border={0} />
                        </Link>
                    </div>
                    <div className="card-body d-flex flex-row flex_flow_rowwrap">
                        <div className="col-12 col-sm-12 col-md-12">
                            {!setupToken ? (
                                // The module-level setup token (and a direct visit to this URL,
                                // which never had one) doesn't survive a reload - the module is
                                // re-evaluated from scratch - so there's no way to recover the
                                // session at that point. Send the user back to their invite link
                                // instead of a broken QR/MFA form.
                                <>
                                    <h5 className="login-box-msg mb-2">Session expired</h5>
                                    <div className="mb-3 text-center">
                                        Your registration session has expired or this page was opened directly.
                                        Please use the invite link from your email again to finish setting up your account.
                                    </div>
                                    <div className="row">
                                        <div className="col-12 mt-3">
                                            <Link to="/" className="btn btn-blue btn-block">Back to Login</Link>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h5 className="login-box-msg mb-2">Authentication</h5>
                                    <div className="mb-3 text-center">
                                        Your account is protected with multi-factor authentication (MFA)
                                    </div>
                                    <div className="mb-4 text-center fwt_600 f_primary">
                                        Scan the QR code below
                                    </div>
                                    <div className="qrcode_scandiv text-center mt-3 mb-4">
                                        {qrCode ? (
                                            <img src={qrCode} alt="QR Code" />
                                        ) : qrError ? (
                                            <p>Failed to load QR code.</p>
                                        ) : (
                                            <p>Loading QR Code...</p>
                                        )}
                                    </div>
                                    <div className="mb-3 text-center">
                                        To finish signing in, enter the code from your MFA device below.
                                    </div>
                                    <form onSubmit={handleMfaSubmit}>
                                        <div className="mb-3 text-left fwt_600">
                                            MFA code
                                        </div>
                                        <div className="input-group mb-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter Code"
                                                value={mfaToken}
                                                onChange={(e) => setMfaToken(e.target.value)}
                                            />
                                        </div>
                                        <div className="row">
                                            <div className="col-12 mt-3">
                                                <button type="submit" className="btn btn-blue btn-block">Submit</button>
                                            </div>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Authentication;
